import { Grid, makeStyles } from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { getNodeById } from '../../api/node';
import { ComponentManagementActionType } from '../../reducers/comp.actions';
import EditorialContentPanel from './EditorialContentPanel';
import GoldenRecordEditorV2 from './GoldenRecordEditorV2';
import { EditorialType, Node } from '../../domain/domain';
import { UiActionType } from '../../reducers/ui.reducer';

const useStyles = makeStyles(theme => ({
    goldenRecordEditor: {
        paddingRight: theme.spacing(5)
    }
}));

type Props = {
    nodeId: number | undefined,
    onRejectNode: (node: Node) => void,
    onApproveNode: (node: Node) => void,
    onApproveEditorialContent: (node: Node, editorialType: EditorialType) => void,
    onRejectEditorialContent: (node: Node, editorialType: EditorialType) => void
}

const EditorPanel : React.FC<Props> = ({nodeId, onRejectNode, onApproveNode, onApproveEditorialContent, onRejectEditorialContent}) => {
    const classes = useStyles();
    const dispatch = useDispatch();
    const [loading, setLoading] = useState<boolean>(false);

    useEffect(() => {
        if (nodeId === undefined) {
            return;
        }
        setLoading(true);
        getNodeById(nodeId).then(response => {
            dispatch({ type: ComponentManagementActionType.EDITOR_NODE_UPDATED, editorNode: response.data });
        }).catch(error => {
            console.error(error);
            dispatch({type: UiActionType.SNACKBAR_STATE_CHANGED, newState: { open: true, message: "Node content could not be loaded, please try again later.", severity: "error"}})
        }).finally(() => {
            setLoading(false);
        })
    }, [nodeId]);

    if (nodeId === undefined) {
        return <></>
    }

    function normalizeTableAlignment(html: string): string {
        const template = document.createElement('template');
        template.innerHTML = html;

        const content = template.content;

        content.querySelectorAll('tr[valign]').forEach((tr) => {
            const valign = tr.getAttribute('valign');
            if (valign) {
                tr.removeAttribute('valign');
                tr.querySelectorAll('td').forEach((td) => {
                    (td as HTMLElement).style.verticalAlign = valign;
                });
            }
        });

        content.querySelectorAll('td').forEach((td) => {
            const align = td.getAttribute('align')?.toLowerCase() || 'left';
            const valign = td ? td.style.verticalAlign : 'top';

            if (align) {
                td.removeAttribute('align');

                let div = td.querySelector('div');
                if (!div) {
                    div = document.createElement('div');
                    while (td.firstChild) {
                        div.appendChild(td.firstChild);
                    }
                    div.setAttribute('data-cell-align', `${valign}-${align}`)
                    td.appendChild(div);
                }

                (div as HTMLElement).style.textAlign = align;
            }
        });

        return template.innerHTML;
    }

    const parseHtmlForEditor = (html: string): string => {
        if (!html) return '';

        html = normalizeTableAlignment(html);
        html = html.replace(/<pl\s*\/>/g, "\u{1F3F7}");
        html = html.replace(/<bl\s*\/>/g, "→");
        html = html.replace(/<bl_1\s*\/>/g, "↦");
        html = html.replace(/<dl\s*\/>/g, "…");

        const replacedHtml = html
            .replace(/<b\b[^>]*>(.*?)<\/b>/gi, '<strong>$1</strong>')
            .replace(/<s\b[^>]*>(.*?)<\/s>/gi, '<del>$1</del>');
        const isWrappedInPTag = /^<p(?:\s+[^>]+)?>[\s\S]*<\/p>$/i.test(replacedHtml);
        const matchResult = replacedHtml.match(/^<p[^>]*>/i);
        const openingPTag = matchResult ? matchResult[0] : '<p>';
        const unwrappedHtml = isWrappedInPTag ? replacedHtml.slice(openingPTag.length, -4) : replacedHtml;

        const parsedHtml = unwrappedHtml.replace(/<t><\/t>/g, '\u00A0\u00A0\u00A0\u00A0');
        const wrappedHtml = `${openingPTag}${parsedHtml}</p>`;

        return wrappedHtml;
    };


    const replaceStrongWithBold = (content: string): string => {
        return content
            .replace(/<strong\b[^>]*>(.*?)<\/strong>/gi, '<b>$1</b>')
            .replace(/<del\b[^>]*>(.*?)<\/del>/gi, '<s>$1</s>');
    };

    const replaceSpecialCharactersAndWhitespace = (content: string): string => {
        return content
            .replace(/&amp;/g, '&')
            .replace(/&nbsp;&nbsp;&nbsp;&nbsp;/g, '\u2003')
            .replace(/&nbsp;/g, '\u00A0')
            .replace(/\u2003/g, '<t></t>')
            .replace(/\u{1F3F7}/ug, '<pl/>')
            .replace(/→/g, '<bl/>')
            .replace(/↦/g, '<bl_1/>')
            .replace(/…/g, '<dl/>')
            .replace(/<br>/g, '<br/>');
    };

    return (
        <Grid container direction='column' spacing={1} wrap='nowrap'>
            <Grid item style={{flexGrow: 1}}>
                <GoldenRecordEditorV2
                    className={classes.goldenRecordEditor}
                    onRejectNode={onRejectNode}
                    onApproveNode={onApproveNode}
                    parseHtmlForEditor={parseHtmlForEditor}
                    replaceStrongWithBold={replaceStrongWithBold}
                    sanitizeContent={replaceSpecialCharactersAndWhitespace}
                />
            </Grid>
            <Grid item>
                <EditorialContentPanel
                    onApproveEditorialContent={onApproveEditorialContent}
                    onRejectEditorialContent={onRejectEditorialContent}
                    parseHtmlForEditor={parseHtmlForEditor}
                    replaceStrongWithBold={replaceStrongWithBold}
                    sanitizeContent={replaceSpecialCharactersAndWhitespace}
                />
            </Grid>
        </Grid>
    )
}

export default EditorPanel;
